/* ==========================================================================
   Engine Displacement Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-edc]');
  if (!root) return;

  var CM_PER_INCH = 2.54;
  var MM_PER_INCH = 25.4;
  var CC_PER_CI = 16.387064;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function cc(boreIn, strokeIn, cyl) {
    var r = (boreIn * CM_PER_INCH) / 2;
    return Math.PI * r * r * (strokeIn * CM_PER_INCH) * cyl;
  }

  function compute(input) {
    var boreIn = input.boreUnit === 'mm' ? input.bore / MM_PER_INCH : input.bore;
    var strokeIn = input.strokeUnit === 'mm' ? input.stroke / MM_PER_INCH : input.stroke;
    if (!isFinite(boreIn) || boreIn <= 0) return { ok: false, errors: ['Enter the bore.'] };
    if (!isFinite(strokeIn) || strokeIn <= 0) return { ok: false, errors: ['Enter the stroke.'] };
    var cyl = isFinite(input.cyl) && input.cyl >= 1 ? Math.round(input.cyl) : 1;

    var baseCc = cc(boreIn, strokeIn, cyl);
    var baseCi = baseCc / CC_PER_CI;
    var perCyl = baseCc / cyl;
    var ratio = boreIn / strokeIn;

    var shape = ratio > 1.05 ? 'Oversquare, a bore larger than the stroke. Revs more freely.'
              : (ratio < 0.95 ? 'Undersquare, a stroke longer than the bore. Favours low end torque.'
                              : 'Square, bore and stroke effectively equal.');

    var figures = [
      { label: 'Displacement', value: num(baseCi, 1) + ' ci',
        sub: comma(Math.round(baseCc)) + ' cc, ' + num(baseCc / 1000, 2) + ' litres.' },
      { label: 'Per cylinder', value: num(perCyl, 1) + ' cc',
        sub: num(perCyl / CC_PER_CI, 2) + ' cubic inches each across ' + cyl + ' cylinders.' }
    ];

    var rows = [
      { head: 'As entered' },
      { label: 'Bore', value: num(boreIn, 3) + ' in  (' + num(boreIn * MM_PER_INCH, 2) + ' mm)' },
      { label: 'Stroke', value: num(strokeIn, 3) + ' in  (' + num(strokeIn * MM_PER_INCH, 2) + ' mm)' },
      { label: 'Cylinders', value: String(cyl) },
      { label: 'Bore to stroke', value: num(ratio, 3) + ' : 1' },
      { head: 'Displacement' },
      { label: 'Cubic inches', value: num(baseCi, 2) },
      { label: 'Cubic centimetres', value: comma(Math.round(baseCc)) },
      { label: 'Litres', value: num(baseCc / 1000, 3) }
    ];

    var warnings = [];
    var finalBore = boreIn, finalStroke = strokeIn, changed = false;

    if (isFinite(input.overbore) && input.overbore > 0) {
      finalBore = boreIn + input.overbore;
      changed = true;
    }
    if (isFinite(input.newStroke) && input.newStroke > 0) {
      finalStroke = input.newStroke;
      changed = true;
    }

    if (changed) {
      var newCc = cc(finalBore, finalStroke, cyl);
      var newCi = newCc / CC_PER_CI;
      rows.push({ head: 'After the change' });
      if (input.overbore > 0) rows.push({ label: 'New bore', value: num(finalBore, 3) + ' in  (+' + num(input.overbore, 3) + ')' });
      if (input.newStroke > 0) rows.push({ label: 'New stroke', value: num(finalStroke, 3) + ' in' });
      rows.push({ label: 'New displacement', value: num(newCi, 1) + ' ci  (' + num(newCc / 1000, 2) + ' L)' });
      rows.push({ label: 'Gain', value: signed(newCi - baseCi, 1) + ' ci  (' + signed(((newCc / baseCc) - 1) * 100, 1) + '%)' });
      figures.push({ label: 'After the change', value: num(newCi, 1) + ' ci',
        sub: signed(newCi - baseCi, 1) + ' cubic inches, a ' + num(((newCc / baseCc) - 1) * 100, 1) + '% increase.' });
      if (input.overbore > 0.06) {
        warnings.push('An overbore past about 0.060 inch leaves thin cylinder walls on many blocks. Sonic test the block before committing to it.');
      }
    }

    return {
      ok: true, errors: [], warnings: warnings,
      status: { key: 'good', label: num(baseCi, 1) + ' ci / ' + num(baseCc / 1000, 1) + 'L', note: shape },
      figures: figures, rows: rows
    };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "bore",
          "key": "b",
          "type": "number"
      },
      {
          "name": "boreUnit",
          "key": "bu",
          "type": "select"
      },
      {
          "name": "stroke",
          "key": "s",
          "type": "number"
      },
      {
          "name": "strokeUnit",
          "key": "su",
          "type": "select"
      },
      {
          "name": "cyl",
          "key": "n",
          "type": "number"
      },
      {
          "name": "overbore",
          "key": "o",
          "type": "number"
      },
      {
          "name": "newStroke",
          "key": "ns",
          "type": "number"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-edc__figure">'
        + '<div class="pc-edc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-edc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-edc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-edc__row pc-edc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-edc__row">'
        + '<span class="pc-edc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-edc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_EDC = { compute: compute };
})();
